/**
 * Zentrale Formatierungsfunktionen für Preise und Zahlen
 * Konsolidiert die formatPrice-Logik, die zuvor in 15+ Dateien dupliziert war
 */

export interface FormatPriceOptions {
  /** Währungscode (EUR, USD) - wenn gesetzt, wird Währungssymbol angezeigt */
  currency?: 'EUR' | 'USD' | string;
  /** Position des Währungssymbols (default: 'auto' = Intl entscheidet) */
  symbolPosition?: 'before' | 'after' | 'auto';
  /** Nur das Symbol ohne Intl-Formatierung (z.B. "123,45 $" statt "123,45 $") */
  symbolOnly?: boolean;
  /** Minimale Nachkommastellen (default: 2) */
  minDecimals?: number;
  /** Maximale Nachkommastellen (default: 2) */
  maxDecimals?: number;
  /** Automatische Dezimalstellen basierend auf Betrag (>=1000 = 0, sonst 2) */
  autoDecimals?: boolean;
  /** Fallback-Wert wenn null/undefined (default: '-') */
  fallback?: string;
}

/**
 * Formatiert einen Preis/Betrag für die Anzeige
 *
 * @param amount - Der zu formatierende Betrag
 * @param options - Formatierungsoptionen
 * @returns Formatierter String
 *
 * @example
 * // Standard mit Währung
 * formatPrice(123.45, { currency: 'EUR' }) // "123,45 €"
 *
 * // Ohne Währung
 * formatPrice(123.45) // "123,45"
 *
 * // Mit Symbol am Ende
 * formatPrice(123.45, { currency: 'USD', symbolPosition: 'after' }) // "123,45 $"
 *
 * // Automatische Dezimalstellen
 * formatPrice(1500, { autoDecimals: true }) // "1.500"
 * formatPrice(99.5, { autoDecimals: true }) // "99,50"
 */
export function formatPrice(
  amount: number | string | null | undefined,
  options: FormatPriceOptions = {}
): string {
  const {
    currency,
    symbolPosition = 'auto',
    symbolOnly = false,
    minDecimals = 2,
    maxDecimals = 2,
    autoDecimals = false,
    fallback = '-'
  } = options;

  // Null/undefined handling
  if (amount === null || amount === undefined) {
    return fallback;
  }

  // String zu Number konvertieren
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;

  if (isNaN(numAmount)) {
    return fallback;
  }

  // Dezimalstellen bestimmen
  let effectiveMinDecimals = minDecimals;
  let effectiveMaxDecimals = maxDecimals;

  if (autoDecimals) {
    if (numAmount >= 1000) {
      effectiveMinDecimals = 0;
      effectiveMaxDecimals = 0;
    } else {
      effectiveMinDecimals = 2;
      effectiveMaxDecimals = 2;
    }
  }

  // Mit Währung formatieren
  if (currency && !symbolOnly) {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: currency.toUpperCase(),
      minimumFractionDigits: effectiveMinDecimals,
      maximumFractionDigits: effectiveMaxDecimals
    }).format(numAmount);
  }

  // Nur Zahl formatieren
  const formattedNumber = numAmount.toLocaleString('de-DE', {
    minimumFractionDigits: effectiveMinDecimals,
    maximumFractionDigits: effectiveMaxDecimals
  });

  // Mit Symbol (manuell positioniert)
  if (currency && symbolOnly) {
    const symbol = currency === 'USD' ? '$' : currency === 'EUR' ? '€' : currency;
    if (symbolPosition === 'before') {
      return `${symbol}${formattedNumber}`;
    }
    return `${formattedNumber} ${symbol}`;
  }

  return formattedNumber;
}

/**
 * Formatiert einen Preis ohne Währungssymbol
 * Shortcut für formatPrice ohne currency Option
 */
export function formatPriceNumber(
  amount: number | string | null | undefined,
  options: Omit<FormatPriceOptions, 'currency' | 'symbolPosition' | 'symbolOnly'> = {}
): string {
  return formatPrice(amount, options);
}

/**
 * Formatiert einen Preis mit EUR-Währung
 */
export function formatPriceEUR(
  amount: number | string | null | undefined,
  options: Omit<FormatPriceOptions, 'currency'> = {}
): string {
  return formatPrice(amount, { ...options, currency: 'EUR' });
}

/**
 * Formatiert einen Preis mit USD-Währung
 */
export function formatPriceUSD(
  amount: number | string | null | undefined,
  options: Omit<FormatPriceOptions, 'currency'> = {}
): string {
  return formatPrice(amount, { ...options, currency: 'USD' });
}

/**
 * Alias für formatPrice mit Währung (für Abwärtskompatibilität)
 * Ersetzt die duplizierten formatCurrency-Funktionen in invoiceService, paymentService
 */
export function formatCurrency(
  amount: number | string | null | undefined,
  currency: string = 'EUR'
): string {
  return formatPrice(amount, { currency });
}

/**
 * Formatiert einen Preis mit Symbol am Ende (z.B. "123,45 $")
 */
export function formatPriceWithSymbol(
  amount: number | null | undefined,
  currencySymbol: string
): string {
  if (amount === null || amount === undefined) return '-';

  const formattedNumber = amount.toLocaleString('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

  return `${formattedNumber} ${currencySymbol}`;
}

/**
 * Formatiert eine Zahl kompakt mit Suffix (K, M, B, T) - OHNE Waehrungs-
 * symbol. Gleiche Logik wie formatPriceCompactWithSymbol, nur ohne Symbol.
 * Praktisch fuer Ranges "1,2K - 5,3K $" oder andere Kontexte.
 */
export function formatPriceNumberCompact(
  amount: number | null | undefined
): string {
  if (amount === null || amount === undefined) return '-';

  const abs = Math.abs(amount);
  const sign = amount < 0 ? '-' : '';

  let value: number;
  let suffix: string;

  if (abs >= 1e12) {
    value = amount / 1e12;
    suffix = 'T';
  } else if (abs >= 1e9) {
    value = amount / 1e9;
    suffix = 'B';
  } else if (abs >= 1e6) {
    value = amount / 1e6;
    suffix = 'M';
  } else if (abs >= 1e3) {
    value = amount / 1e3;
    suffix = 'K';
  } else {
    return `${sign}${Math.abs(amount).toLocaleString('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }

  const absValue = Math.abs(value);
  let decimals: number;
  if (suffix === 'K' && absValue >= 100) {
    decimals = 0;
  } else if (suffix === 'K' && absValue >= 10) {
    decimals = 1;
  } else {
    decimals = 2;
  }

  return `${sign}${absValue.toLocaleString('de-DE', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}${suffix}`;
}

/**
 * Formatiert einen Preis kompakt mit Suffix (K, M, B, T) und Waehrungssymbol
 * am Ende. Intelligente Dezimalstellen:
 *   - Unter 1000: 2 Dezimalstellen (z.B. "42,50 $")
 *   - K-Bereich: 1-2 Dezimalstellen je nach Wert (z.B. "1,23K $", "42,5K $")
 *   - M/B/T-Bereich: 2 Dezimalstellen (z.B. "1,23M $")
 * Waehrungssymbol wird IMMER hinter der Zahl gesetzt.
 *
 * Beispiele:
 *   formatPriceCompactWithSymbol(42.5, '$')      -> "42,50 $"
 *   formatPriceCompactWithSymbol(1234, '$')      -> "1,23K $"
 *   formatPriceCompactWithSymbol(48111, '$')     -> "48,1K $"
 *   formatPriceCompactWithSymbol(1234567, '$')   -> "1,23M $"
 *   formatPriceCompactWithSymbol(2500000000, '€')-> "2,50B €"
 */
export function formatPriceCompactWithSymbol(
  amount: number | null | undefined,
  currencySymbol: string
): string {
  if (amount === null || amount === undefined) return '-';

  const abs = Math.abs(amount);
  const sign = amount < 0 ? '-' : '';

  let value: number;
  let suffix: string;

  if (abs >= 1e12) {
    value = amount / 1e12;
    suffix = 'T';
  } else if (abs >= 1e9) {
    value = amount / 1e9;
    suffix = 'B';
  } else if (abs >= 1e6) {
    value = amount / 1e6;
    suffix = 'M';
  } else if (abs >= 1e3) {
    value = amount / 1e3;
    suffix = 'K';
  } else {
    // Unter 1000 - normale Formatierung mit 2 Dezimalstellen
    const formatted = Math.abs(amount).toLocaleString('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return `${sign}${formatted} ${currencySymbol}`;
  }

  // Intelligente Dezimalstellen im K-Bereich:
  // - 1.0K bis 9.99K -> 2 Dezimalstellen (z.B. "1,23K")
  // - 10K bis 99.9K  -> 1 Dezimalstelle (z.B. "48,1K")
  // - 100K+          -> 0-1 Dezimalstellen (z.B. "250K", "1,23M fuer groesser)
  const absValue = Math.abs(value);
  let decimals: number;
  if (suffix === 'K' && absValue >= 100) {
    decimals = 0;
  } else if (suffix === 'K' && absValue >= 10) {
    decimals = 1;
  } else {
    decimals = 2;
  }

  const formattedValue = absValue.toLocaleString('de-DE', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  return `${sign}${formattedValue}${suffix} ${currencySymbol}`;
}

/**
 * Formatiert große Zahlen mit Suffix (K, M, B, T)
 * z.B. 1500000 -> "1.50M"
 */
export function formatLargeNumber(value: number): string {
  if (value >= 1e12) {
    return `${(value / 1e12).toFixed(2)}T`;
  } else if (value >= 1e9) {
    return `${(value / 1e9).toFixed(2)}B`;
  } else if (value >= 1e6) {
    return `${(value / 1e6).toFixed(2)}M`;
  } else if (value >= 1e3) {
    return `${(value / 1e3).toFixed(2)}K`;
  }
  return value.toString();
}

/**
 * Formatiert einen Prozentsatz mit Vorzeichen
 * z.B. 5.5 -> "+5,50%", -3.2 -> "-3,20%"
 */
export function formatPercentage(value: number): string {
  const sign = value >= 0 ? '+' : '';
  return `${sign}${value.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
}

/**
 * Formatiert Dateigrößen für die Anzeige
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/**
 * Formatiert Lesezeit für die Anzeige
 */
export function formatReadingTime(minutes: number): string {
  if (minutes < 1) return 'Weniger als 1 Min';
  if (minutes === 1) return '1 Min';
  return `${minutes} Min`;
}
